import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  input,
  linkedSignal,
  type OnChanges,
  type SimpleChanges,
} from '@angular/core';
import { MatSlideToggle, type MatSlideToggleChange } from '@angular/material/slide-toggle';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ResolveTranslateAbleTextPipe, type TranslateAbleText } from '@pct-ng/i18n';
import { AbstractWithDelegatedFormControlComponent } from '../../reactive-forms/control/abstract-with-delegated-form-control.component';

@Component({
  selector: 'pct-form-string-slide-toggle',
  templateUrl: './string-slide-toggle.component.html',
  imports: [MatSlideToggle, ResolveTranslateAbleTextPipe],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StringSlideToggleComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StringSlideToggleComponent
  extends AbstractWithDelegatedFormControlComponent<string | null>
  implements OnChanges
{
  readonly trueValue = input.required<string>();
  readonly falseValue = input.required<string | null>();
  readonly defaultValue = input.required<string | null>();
  readonly label = input.required<TranslateAbleText>();

  protected currentValue = linkedSignal(() => this.defaultValue() === this.trueValue());

  protected onChangeValue({ checked }: MatSlideToggleChange): void {
    this.changeValue(checked ? this.trueValue() : this.falseValue());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['defaultValue'] !== undefined &&
      !changes['defaultValue'].firstChange &&
      changes['trueValue'] !== undefined &&
      !changes['trueValue'].firstChange &&
      changes['falseValue'] !== undefined &&
      !changes['falseValue'].firstChange
    ) {
      /**
       * Throwing an error is necessary because these values populate the material slide element
       * and an event can already be emitted. Since we can't predict this in advance,
       * we might end up in an inconsistent state—for example,
       * if we emit a `trueValue` during a control change
       * and then replace the set of possible values afterward.
       */
      throw new Error('Double init default value or true value');
    }
  }

  writeValue(obj: string | null): void {
    this.currentValue.set(this.trueValue() === obj);
  }
}
